import { useI18n } from "@/i18n/I18nProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Eye, ShieldCheck, Scale, FileText, CheckCircle2, ArrowRight, ArrowLeft, BookOpen, Users, HandHeart, MapPin, BadgeCheck, XCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { getCmsLayout, CmsLayout } from "@/data/cmsStore";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const ObservateurPage = () => {
  const { isRtl, lang, t } = useI18n();
  const o = t.observateur;
  const Arrow = isRtl ? ArrowLeft : ArrowRight;
  const [layout, setLayout] = useState<CmsLayout>([]);

  useEffect(() => {
    setLayout(getCmsLayout("observateur"));
    
    const handleLayoutChange = (e: any) => {
      if (e.detail?.page === "observateur") {
        setLayout(getCmsLayout("observateur"));
      }
    };
    window.addEventListener("cmsLayoutChanged", handleLayoutChange);
    return () => window.removeEventListener("cmsLayoutChanged", handleLayoutChange);
  }, []);

  return (
    <div className="min-h-screen bg-background" dir={isRtl ? 'rtl' : 'ltr'}>
      <Navbar />
      
      {layout.length > 0 ? (
        layout.map((section) => {
          if (!section.visible) return null;
          
          switch (section.type) {
            case "ObserverHeroSection":
              return (
                <section key={section.id} className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-primary/5 via-accent/5 to-background">
        <div className="container relative z-10 max-w-4xl text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center justify-center p-3 sm:p-4 bg-primary/10 rounded-2xl sm:rounded-full mb-6">
              <Eye className="w-10 h-10 sm:w-12 sm:h-12 text-primary" />
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6 leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
              {o.heroTitle}
            </h1>
            <div className="bg-primary text-primary-foreground font-bold font-display px-5 py-2 text-lg rounded-full inline-block mb-6 shadow-sm">
              {o.heroBadge}
            </div>
            <p className="text-xl sm:text-2xl text-foreground/80 font-body max-w-3xl mx-auto leading-relaxed">
              {o.heroDesc}
            </p>
          </motion.div>
        </div>
                </section>
              );

            case "ObserverTabsSection":
              return (
                <section key={section.id} className="py-16 md:py-24">
        <div className="container max-w-5xl">

          {/* Section: Definition */}
          <div className="mb-16 bg-primary text-primary-foreground rounded-3xl p-8 md:p-12 shadow-lg text-start">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold mb-6 leading-tight">
              {o.definitionTitle}
            </h2>
            <p className="text-lg md:text-xl font-body leading-relaxed opacity-90 leading-loose">
              {o.definitionDesc}
            </p>
          </div>

          {/* Section 1: Qui peut observer ? */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-10 text-foreground flex items-center gap-4">
              <span className="bg-primary/20 p-3 rounded-2xl text-primary"><Users className="w-8 h-8" /></span>
              {o.section1Title}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
               <Card className="bg-background shadow-lg rounded-3xl border-border/60 hover:border-primary/50 transition-colors">
                 <CardContent className="p-8">
                   <ShieldCheck className="w-12 h-12 text-primary mb-6" />
                   <h3 className="font-display font-bold text-2xl mb-4 text-foreground">{o.institutions[0]?.title}</h3>
                   <p className="text-lg text-foreground/80 font-body leading-relaxed">{o.institutions[0]?.desc}</p>
                 </CardContent>
               </Card>
               <Card className="bg-background shadow-lg rounded-3xl border-border/60 hover:border-accent/50 transition-colors">
                 <CardContent className="p-8">
                   <HandHeart className="w-12 h-12 text-accent mb-6" />
                   <h3 className="font-display font-bold text-2xl mb-4 text-foreground">{o.institutions[1]?.title}</h3>
                   <p className="text-lg text-foreground/80 font-body leading-relaxed">{o.institutions[1]?.desc}</p>
                 </CardContent>
               </Card>
               <Card className="bg-background shadow-lg rounded-3xl border-border/60 hover:border-blue-500/50 transition-colors">
                 <CardContent className="p-8">
                   <MapPin className="w-12 h-12 text-blue-500 mb-6" />
                   <h3 className="font-display font-bold text-2xl mb-4 text-foreground">{o.institutions[2]?.title}</h3>
                   <p className="text-lg text-foreground/80 font-body leading-relaxed">{o.institutions[2]?.desc}</p>
                 </CardContent>
               </Card>
            </div>
          </div>

          {/* Section 2: Commission & Accréditation */}
          <div className="mb-20 bg-primary/5 border border-primary/20 rounded-[2.5rem] p-8 md:p-16 shadow-sm">
            <h2 className="text-4xl lg:text-5xl font-display font-bold mb-8 text-foreground text-center leading-tight">
              {o.section2Title}
            </h2>
            <p className="text-center font-body text-xl text-foreground/80 max-w-3xl mx-auto mb-14 leading-relaxed">
              {o.section2Desc}
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-3xl shadow-sm">
                 <h3 className="text-2xl lg:text-3xl font-display font-bold text-foreground mb-6 flex items-center gap-3">
                   <BadgeCheck className="w-8 h-8 text-green-500" /> {o.conditionsTitle}
                 </h3>
                 <ul className="space-y-4 font-body text-foreground/80 text-lg">
                   {o.conditions.map((item, idx) => (
                     <li key={idx} className="flex gap-4"><CheckCircle2 className="w-6 h-6 text-primary shrink-0 mt-1" /> <span className="leading-snug">{item}</span></li>
                   ))}
                 </ul>
              </div>
              <div className="bg-white p-8 rounded-3xl shadow-sm">
                 <h3 className="text-2xl lg:text-3xl font-display font-bold text-foreground mb-6 flex items-center gap-3">
                   <BookOpen className="w-8 h-8 text-blue-500" /> {o.modalitesTitle}
                 </h3>
                 <ul className="space-y-4 font-body text-foreground/80 text-lg">
                   {o.modalites.map((item, idx) => (
                     <li key={idx} className="flex gap-4"><Arrow className="w-6 h-6 text-accent shrink-0 mt-1" /> <span className="leading-snug">{item}</span></li>
                   ))}
                 </ul>
              </div>
            </div>
          </div>

          {/* Section 3: Droits et Obligations */}
          <div className="mb-24">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-10 text-foreground flex items-center gap-4">
              <span className="bg-accent/20 p-3 rounded-2xl text-accent"><Scale className="w-8 h-8" /></span>
              {o.section3Title}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Droits */}
              <div className="bg-background border-4 border-primary/10 rounded-[2rem] p-8 shadow-sm">
                 <h3 className="text-3xl font-display font-bold text-primary mb-6">{o.droitsTitle}</h3>
                 <ul className="space-y-5 font-body text-foreground/80 text-lg">
                   {o.droits.map((item, idx) => (
                     <li key={idx} className="flex gap-4"><CheckCircle2 className="w-7 h-7 text-primary shrink-0 mt-0.5" /> <span className="leading-snug">{item}</span></li>
                   ))}
                 </ul>
              </div>
              {/* Obligations */}
              <div className="bg-background border-4 border-destructive/10 rounded-[2rem] p-8 shadow-sm">
                 <h3 className="text-3xl font-display font-bold text-destructive mb-6">{o.obligationsTitle}</h3>
                 <ul className="space-y-5 font-body text-foreground/80 text-lg">
                   {o.obligations.map((item, idx) => (
                     <li key={idx} className="flex gap-4"><XCircle className="w-7 h-7 text-destructive shrink-0 mt-0.5" /> <span className="leading-snug">{item}</span></li>
                   ))}
                 </ul>
              </div>
            </div>
          </div>

          {/* Lien vers la Commission CNDH */}
          <div className="flex flex-col items-center text-center p-12 border-t-2 border-border/50 mt-16 bg-card rounded-[3rem] shadow-sm">
             <a
               href={
                 lang === 'ar' ? 'https://observationelections.cndh.ma/' : 
                 lang === 'zgh' ? 'https://observationelections.cndh.ma/am/' : 
                 'https://observationelections.cndh.ma/fr/'
               }
               target="_blank"
               rel="noopener noreferrer"
               className="group inline-block w-full max-w-2xl"
             >
               <div className="bg-white p-8 md:p-12 rounded-[2rem] shadow-xl hover:shadow-2xl transition-all border border-border/30 transform hover:-translate-y-2 mb-10">
                 <h2 className="text-3xl md:text-4xl font-display font-bold text-blue-900 leading-tight">
                   {o.cndhTitle}
                 </h2>
                 <p className="text-blue-600 mt-6 font-body font-bold text-xl flex items-center justify-center gap-3">
                   {o.cndhBtn} <Arrow className={`w-6 h-6 transition-transform ${isRtl ? 'group-hover:-translate-x-2' : 'group-hover:translate-x-2'}`} />
                 </p>
               </div>
             </a>
             
             <h3 className="text-3xl font-display font-bold text-foreground mb-6">{o.downloadTitle}</h3>
             <p className="text-foreground/80 font-body text-xl max-w-3xl mx-auto leading-relaxed mb-10">
               {o.downloadDesc}
             </p>

             {/* Nouveau bouton Bilan 2021 */}
             <motion.div 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
             >
                <a 
                  href="/observation-2021.html" 
                  className="inline-flex items-center gap-3 bg-secondary text-secondary-foreground font-display font-semibold text-lg py-5 px-10 rounded-full shadow-lg hover:shadow-xl hover:bg-secondary/90 transition-all transform hover:-translate-y-1"
                >
                  <Eye className="w-5 h-5" />
                  {isRtl ? "اطلع على حصيلة الملاحظة لعام 2021" : "Consulter le Bilan de l'Observation 2021"}
                </a>
             </motion.div>
          </div>

        </div>
                </section>
              );

            case "ObserverCTASection":
              return (
                <section key={section.id} className="bg-muted/30 py-12 border-t border-border">
        <div className="container max-w-5xl">
          <h3 className="font-display font-bold text-xl mb-6 text-foreground">
            {o.suggestedTitle}
          </h3>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            <Link to="/voter" className="bg-background border border-border p-5 rounded-2xl hover:shadow-md transition-shadow group">
              <CheckCircle2 className="w-8 h-8 text-accent mb-3 group-hover:scale-110 transition-transform" />
              <h4 className="font-display font-bold text-foreground mb-2">{o.sugVoterTitle}</h4>
              <p className="text-sm font-body text-muted-foreground">{o.sugVoterDesc}</p>
            </Link>
            
            <Link to="/quiz" className="bg-background border border-border p-5 rounded-2xl hover:shadow-md transition-shadow group">
              <FileText className="w-8 h-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
              <h4 className="font-display font-bold text-foreground mb-2">{o.sugQuizTitle}</h4>
              <p className="text-sm font-body text-muted-foreground">{o.sugQuizDesc}</p>
            </Link>

            <Link to="/engagement" className="bg-background border border-border p-5 rounded-2xl hover:shadow-md transition-shadow group">
              <ShieldCheck className="w-8 h-8 text-green-500 mb-3 group-hover:scale-110 transition-transform" />
              <h4 className="font-display font-bold text-foreground mb-2">{o.sugPartiTitle}</h4>
              <p className="text-sm font-body text-muted-foreground">{o.sugPartiDesc}</p>
            </Link>
          </div>
        </div>
      </section>
              );

            case "CustomRichSection": {
              const langHtml = lang === "fr" ? section.content.html_fr : lang === "ar" ? section.content.html_ar : section.content.html_fr;
              return (
                <div key={section.id} className={`${section.content.bg_color || 'bg-background'} ${section.content.padding_y || 'py-12'}`}>
                  <div className="container mx-auto px-4">
                    <div dangerouslySetInnerHTML={{ __html: langHtml || '' }} />
                  </div>
                </div>
              );
            }

            default:
              return null;
          }
        })
      ) : (
        <div className="h-screen flex items-center justify-center">
          <span className="animate-pulse">Chargement de la page...</span>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ObservateurPage;
